import { faSearch, faTimes } from "@outschool/icons";
/**
 * Figma file: https://www.figma.com/file/vG9GNXiYwiuPpss0kVT8se/%E2%AD%90-Backpack-Components-(2023)?type=design&node-id=2545-32701&mode=design&t=020qhwN2bNZFv5CL-0
 *
 */
import React from "react";

import { THEME } from "../../theme";
import { gray, peacock } from "../../theme/colors";
import { SxProps } from "../..";
import Box from "../Box/Box";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import IconButton from "../IconButton/IconButton";
import Input, { InputProps } from "../Input/Input";

type OSInputProps = {
  getInputProps?: () => object;
};

enum SearchBoxVariant {
  inApp = "InApp",
  navbar = "Navbar",
}

export interface SearchBoxProps {
  /**
   * Function to handle the form sending
   */
  onSubmit: (e: any) => void;
  /**
   * Function to clear out the input value
   */
  onClear: () => void;
  /**
   * Label to be set on the inApp variant button
   */
  searchLabel?: string;
  /**
   * Props forwarded to the input field
   */
  inputProps?: OSInputProps & InputProps;
  /**
   * Ref to the clear button that shows up when the value is present
   */
  clearSearchInputBoxRef?: React.RefObject<HTMLInputElement>;
  /**
   * String for the aria label of the clear search icon
   */
  clearSearchAriaLabel?: string;
}

export function NavbarSearchBox(props: SearchBoxProps) {
  return <SearchBox {...props} variant={SearchBoxVariant.navbar} />;
}

export function InAppSearchBox(props: SearchBoxProps) {
  return <SearchBox {...props} variant={SearchBoxVariant.inApp} />;
}

export type SearchBoxComponentType =
  | typeof NavbarSearchBox
  | typeof InAppSearchBox;

function SearchBox({
  inputProps: {
    ref: inputRef,
    placeholder,
    value,
    onBlur,
    onFocus,
    onChange,
    spellCheck,
    getInputProps = () => ({}),
  } = {},
  onSubmit,
  onClear,
  clearSearchInputBoxRef,
  clearSearchAriaLabel = "Clear Search",
  searchLabel = "Search",
  variant,
}: SearchBoxProps & { variant: SearchBoxVariant }) {
  return (
    <Box
      sx={{
        "&:focus-within": {
          borderColor: "primary.main",
        },
        padding: "2px",
        border: "2px solid transparent",
        borderRadius: "999px",
        width: "100%",
      }}
    >
      <Box
        component="form"
        sx={{
          backgroundColor: "white",
          border: "2px solid",
          borderColor: gray[700],
          borderRadius: "999px",
          flex: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding:
            variant === SearchBoxVariant.inApp
              ? "8px 8px 8px 20px"
              : "4px 8px 4px 20px",
          transition: "box-shadow 0.3s ease-in-out",
          "&:hover": {
            boxShadow:
              "0px 8px 16px 0px rgba(0, 0, 0, 0.08), 0px 0px 4px 0px rgba(0, 0, 0, 0.04);",
          },
        }}
        onSubmit={onSubmit}
      >
        {variant === SearchBoxVariant.inApp && (
          <Responsive Desktop={<SearchIcon ariaLabel={searchLabel} />} />
        )}
        <Input
          disableUnderline
          fullWidth
          size={variant === SearchBoxVariant.navbar ? "small" : "medium"}
          variant="filled"
          type="text"
          inputProps={{
            sx: {
              paddingY: "5px",
              typography: "h6",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            },
          }}
          sx={{
            "&::placeholder": {
              color: gray[600],
            },
            "&:focus": {
              outline: "none",
              border: "none",
            },
            textOverflow: "ellipsis",
            background: "none",
            color: gray[900],
          }}
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onChange}
          spellCheck={spellCheck}
          {...getInputProps()}
        />

        {variant === SearchBoxVariant.navbar && (
          <>
            <ClearInputButton
              value={value}
              iconRef={clearSearchInputBoxRef}
              onClear={onClear}
              ariaLabel={clearSearchAriaLabel}
              sx={{
                color: gray[900],
                width: "3.4rem",
                height: "3.4rem",
              }}
            />
            <SearchIcon ariaLabel={searchLabel} />
          </>
        )}
        {variant === SearchBoxVariant.inApp && (
          <>
            <ClearInputButton
              value={value}
              iconRef={clearSearchInputBoxRef}
              onClear={onClear}
              ariaLabel={clearSearchAriaLabel}
              sx={theme => ({
                color: gray[900],
                width: "4.4rem",
                height: "4.4rem",
                marginRight: "1.6rem",
                [theme.breakpoints.down("md")]: {
                  width: "3.4rem",
                  height: "3.4rem",
                  marginRight: "0.4rem",
                },
              })}
            />
            <Responsive
              Desktop={<SearchButton label={searchLabel} onSubmit={onSubmit} />}
              Mobile={
                <SearchIcon
                  ariaLabel={clearSearchAriaLabel}
                  sx={{
                    color: gray[0],
                    backgroundColor: peacock[700],
                    boxShadow: "0px 0px 2px 0px #6161F2",
                  }}
                />
              }
            />
          </>
        )}
      </Box>
    </Box>
  );
}

function ClearInputButton({
  value,
  iconRef,
  onClear,
  ariaLabel,
  sx,
}: {
  value: string;
  ariaLabel: string;
  iconRef?: React.Ref<HTMLInputElement>;
  onClear?: () => void;
  sx?: SxProps;
}) {
  if (!value) {
    return null;
  }

  return (
    <IconButton ref={iconRef} sx={sx} onClick={onClear} aria-label={ariaLabel}>
      <Icon icon={faTimes} size={"small"} />
    </IconButton>
  );
}

function SearchIcon({ ariaLabel, sx }: { ariaLabel: string; sx?: SxProps }) {
  return (
    <IconButton
      sx={{
        color: gray[900],
        width: "34px",
        height: "34px",
        ...sx,
      }}
      aria-label={ariaLabel}
      type="submit"
    >
      <Icon icon={faSearch} size="small" />
    </IconButton>
  );
}

function SearchButton({
  label,
  onSubmit,
}: {
  label: string;
  onSubmit: (e: any) => void;
}) {
  return (
    <Button size="large" variant="contained" onClick={onSubmit}>
      {label}
    </Button>
  );
}

const desktopOnly = {
  [THEME.breakpoints.down("md")]: {
    display: "none",
  },
};

const mobileOnly = {
  [THEME.breakpoints.up("md")]: {
    display: "none",
  },
};

function Responsive({
  Mobile,
  Desktop,
}: {
  Mobile?: React.ReactNode;
  Desktop?: React.ReactNode;
}) {
  const mobile = Mobile ? <Box sx={mobileOnly}>{Mobile}</Box> : null;
  const desktop = Desktop ? <Box sx={desktopOnly}>{Desktop}</Box> : null;
  return (
    <>
      {mobile}
      {desktop}
    </>
  );
}
